// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faKeyboard,
    faWandMagicSparkles,
    faLanguage,
    faGraduationCap,
    faArrowRight,
    faBookOpen,
    faBook,
    faRotate,
    faChartLine,
    faList,
    faBrain,
    faPenNib,
    faSpellCheck,
    faMicrophone,
    faScroll,
} from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import { useAuth } from "../../context/AuthContext";
import { useContinueLearning } from "../../hooks/useContinueLearning";
import { useUserXp } from "../../hooks/useUserXp";
import { useAchievements } from "../../hooks/useAchievements";
import AchievementBadge from "../Achievements/AchievementBadge";
import { hiraganaWritingList, katakanaWritingList } from "../../consts/kanaWritingList";
import { joyoKanji } from "../../consts/joyoKanji";
import { grammarPoints } from "../../consts/grammarPoints";
import { VOCABULARY_TOTAL } from "../../consts/vocabularyTotal";
import { VERB_TOTAL } from "../../consts/verbTotal";
import ProgressRing from "../ProgressRing/ProgressRing";
import styles from "./homePage.module.scss";

const startLearningItems = [
    {
        to: "/lessons",
        icon: faGraduationCap,
        title: "Explore Japanese lessons",
        description: "Build your foundation with guided Japanese lessons.",
        cta: "Start lessons",
        badge: "Start here",
    },
    {
        to: "/kana-writing-practice?script=katakana",
        icon: faPenNib,
        title: "Practice writing katakana and hiragana",
        description: "Watch the stroke order, then draw it.",
        cta: "Start practicing",
    },
    {
        to: "/grammar",
        icon: faSpellCheck,
        title: "Learn Japanese grammar",
        description: "Grammar points by JLPT level, N5 and up.",
        cta: "Browse grammar points",
    },
    {
        to: "/kanji-writing-practice",
        kana: "漢",
        title: "Draw kanji",
        description: "Filter by JLPT level, then draw it.",
        cta: "Start practicing",
    },
    {
        to: "/kanji-memory-quiz",
        icon: faBrain,
        title: "Kanji Memory Quiz",
        description: "Mark each kanji as known or not yet.",
        cta: "Start the quiz",
    },
    {
        to: "/vocabulary",
        icon: faBook,
        title: "Build your vocabulary",
        description: "JLPT-tagged words with readings and meanings.",
        cta: "Browse vocabulary",
    },
    {
        to: "/conjugation",
        icon: faRotate,
        title: "Verb conjugation",
        description: "Full conjugation tables by JLPT level.",
        cta: "Browse conjugations",
    },
    {
        to: "/translate",
        icon: faLanguage,
        title: "Translate Japanese",
        description: "Translate Japanese text or images instantly.",
        cta: "Try the translator",
    },
    {
        to: "/speaking-practice",
        icon: faMicrophone,
        title: "Practice speaking Japanese",
        description: "Say a word, get instant pronunciation feedback.",
        cta: "Start speaking practice",
    },
];

const integrationItems = [
    {
        to: "/wanikani",
        icon: faChartLine,
        title: "Track your WaniKani progress",
        description: "Connect an existing WaniKani account.",
        cta: "Open WaniKani progress",
    },
];

const toolItems = [
    {
        to: "/keyboard",
        icon: faKeyboard,
        title: "Japanese keyboard",
        description: "Tap or type to build Japanese text.",
        cta: "Open the keyboard",
    },
    {
        to: "/converter",
        icon: faWandMagicSparkles,
        title: "Hiragana and kanji converter",
        description: "Convert hiragana to kanji suggestions.",
        cta: "Open the converter",
    },
    {
        to: "/romaji-converter",
        icon: faLanguage,
        title: "Romaji-to-kana converter",
        description: "Romaji to hiragana or katakana.",
        cta: "Open the converter",
    },
];

const referenceItems = [
    {
        to: "/study-materials",
        icon: faBookOpen,
        title: "Recommended Study Materials",
        description: "Trusted Japanese-learning resources, including Genki textbooks and workbooks.",
    },
    { to: "/blog/joyo-kanji-list", icon: faScroll, title: "Jōyō kanji list", description: "2,136 daily-use kanji with meanings." },
    { to: "/blog/full-kanji-list", icon: faList, title: "Full kanji list", description: "All 2,974 kanji in the reference." },
    { to: "/blog", icon: faBookOpen, title: "Blog", description: "Articles about learning Japanese." },
    {
        to: "/jlpt",
        icon: faGraduationCap,
        title: "Take the JLPT",
        description: "Find official test locations, available sessions, host institutions, and registration information by country.",
        accessibleLabel: "Find a test location",
        isNew: true,
    },
];

function PathCard({ item }) {
    return (
        <Link to={item.to} className={styles.pathCard}>
            {item.badge && <span className={styles.pathCardBadge}>{item.badge}</span>}
            <div className={styles.pathCardIconWrapper}>
                {item.kana ? (
                    <span className={styles.pathCardKanaIcon} aria-hidden="true">{item.kana}</span>
                ) : (
                    <FontAwesomeIcon icon={item.icon} className={styles.pathCardIcon} />
                )}
            </div>
            <div className={styles.pathCardBody}>
                <h3 className={styles.pathCardTitle}>{item.title}</h3>
                <p className={styles.pathCardText}>{item.description}</p>
                <span className={styles.pathCardCta}>
                    {item.cta}
                    <FontAwesomeIcon icon={faArrowRight} className={styles.pathCardCtaIcon} />
                </span>
            </div>
        </Link>
    );
}

function ToolCard({ item }) {
    return (
        <Link
            to={item.to}
            className={`${styles.toolCard} ${item.isNew ? styles.toolCardNew : ""}`}
            aria-label={item.isNew ? `${item.accessibleLabel} - new feature` : item.accessibleLabel}
        >
            <div className={styles.pathCardIconWrapper}>
                <FontAwesomeIcon icon={item.icon} className={styles.pathCardIcon} />
            </div>
            <span className={styles.toolCardText}>
                <span className={styles.toolCardTitleRow}>
                    <span className={styles.toolCardTitle}>{item.title}</span>
                    {item.isNew && <span className={styles.toolCardNewBadge}>New!</span>}
                </span>
                <span className={styles.toolCardDescription}>{item.description}</span>
            </span>
        </Link>
    );
}

function ContinueOptionLink({ item }) {
    return (
        <Link to={item.to} className={styles.continueSecondaryLink}>
            {item.percent !== undefined && (
                <ProgressRing percent={item.percent} label={`${item.title} progress`} />
            )}
            <span className={styles.continueSecondaryText}>
                <span className={styles.continueSecondaryTitle}>{item.title}</span>
                <span className={styles.continueSecondaryDetail}>{item.detail}</span>
            </span>
            <span className={styles.continueSecondaryCta}>
                <span className={styles.continueSecondaryCtaLabel}>Continue</span>
                <FontAwesomeIcon icon={faArrowRight} className={styles.pathCardCtaIcon} />
            </span>
        </Link>
    );
}

function ContinueLearningSection({ authStatus, isSigningOut, isReady, recommendation }) {
    const location = useLocation();

    if (!isReady || isSigningOut) {
        return (
            <section className={styles.continueSection} aria-label="Continue learning">
                <div className={styles.continueSpinnerCard} role="status">
                    <span className={styles.continueSpinner} aria-hidden="true" />
                    <span className={styles.srOnly}>{isSigningOut ? "Signing out…" : "Loading your progress..."}</span>
                </div>
            </section>
        );
    }

    if (!recommendation) return null;

    const { primary, secondary } = recommendation;

    if (authStatus !== "authenticated") {
        return (
            <section className={styles.continueSection} aria-labelledby="continue-learning-heading">
                <div className={styles.continueCard}>
                    <div className={styles.continueCardMain}>
                        <p className={styles.continueEyebrow}>{primary.detail}</p>
                        <h2 id="continue-learning-heading" className={styles.continueHeading}>
                            Log in to keep track of your progress
                        </h2>
                        <p className={styles.continueDetail}>
                            Your progress is only saved in this browser right now - log in to keep it safe and
                            pick up where you left off on any device.
                        </p>
                        <Link to="/login" state={{ from: location }} className={styles.continueCta}>
                            Log in
                            <FontAwesomeIcon icon={faArrowRight} className={styles.pathCardCtaIcon} />
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.continueSection} aria-labelledby="continue-learning-heading">
            <div className={styles.continueCard}>
                <div className={styles.continueCardMain}>
                    <p className={styles.continueEyebrow}>Welcome back</p>
                    <h2 id="continue-learning-heading" className={styles.continueHeading}>
                        Continue with {primary.title}
                    </h2>
                    <p className={styles.continueDetail}>{primary.detail}</p>
                    <Link to={primary.to} className={styles.continueCta}>
                        Continue
                        <FontAwesomeIcon icon={faArrowRight} className={styles.pathCardCtaIcon} />
                    </Link>
                </div>
                {secondary.length > 0 && (
                    <ul className={styles.continueSecondaryList}>
                        {secondary.map((item) => (
                            <li key={item.key}>
                                <ContinueOptionLink item={item} />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

function LearningStatsSection({ visible }) {
    const { status, progress, breakdown, hasProgress, refresh } = useUserXp();
    const { unlocked: unlockedAchievements } = useAchievements();
    const highlights = breakdown.entries
        .filter((entry) => entry.xp > 0)
        .sort((a, b) => b.xp - a.xp)
        .slice(0, 3);

    if (!visible) return null;

    return (
        <section className={styles.learningStats} aria-labelledby="learning-stats-heading">
            <div className={styles.learningStatsIntro}>
                <p className={styles.learningStatsEyebrow}>Your learning stats</p>
                <h2 id="learning-stats-heading" className={styles.learningStatsTitle}>Keep building your Japanese</h2>
                <Link to="/profile" className={styles.learningStatsLink}>View full profile</Link>
            </div>
            {!hasProgress && status === "loading" && <p className={styles.learningStatsLoading} role="status">Loading your learning stats...</p>}
            {!hasProgress && status === "error" && (
                <p className={styles.learningStatsError} role="alert">
                    Couldn't load your learning stats. <button type="button" onClick={refresh}>Try again</button>
                </p>
            )}
            {hasProgress && (
                <div className={styles.learningStatsContent}>
                    <div className={styles.learningStatsLevel}>
                        <strong>Level {progress.level}</strong>
                        <span>{progress.xp} XP</span>
                        <progress aria-label="Progress to next level" value={progress.xpIntoLevel} max={progress.xpForNextLevel} />
                        <small>{progress.nextLevelXp - progress.xp} XP to level {progress.level + 1}</small>
                        {unlockedAchievements.length > 0 && (
                            <div className={styles.learningStatsAchievements} aria-label="Achievements earned">
                                {unlockedAchievements.map((achievement) => (
                                    <AchievementBadge key={achievement.id} achievement={achievement} size={30} />
                                ))}
                            </div>
                        )}
                    </div>
                    <ul className={styles.learningStatsBreakdown} aria-label="Top study areas">
                        {highlights.map((entry) => (
                            <li key={entry.feature}>
                                <span>{entry.label}</span>
                                <span className={styles.learningStatsEntryValue}>
                                    <strong>{entry.xp} XP</strong>
                                    {entry.unit && (
                                        <small>{entry.count} {entry.unit}</small>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

function ScriptProgressBar({
    kanaDrawCounts,
    kanjiProgress,
    grammarProgress = {},
    vocabularyProgress = {},
    conjugationProgress = {},
}) {
    const completedKana = (characters) => characters.filter(({ symbol }) => (kanaDrawCounts[symbol] || 0) > 0).length;
    const completionPercent = (completed, total) => Math.round((completed / total) * 100);
    const knownKanji = joyoKanji.filter((symbol) => kanjiProgress[symbol] === "known").length;
    const learnedGrammar = grammarPoints.filter(({ id }) => grammarProgress[id] === "learned").length;
    const learnedVocabulary = Object.values(vocabularyProgress).filter((value) => value === "learned").length;
    const learnedConjugations = Object.values(conjugationProgress).filter((value) => value === "learned").length;
    const scripts = [
        { label: "Hiragana", percent: completionPercent(completedKana(hiraganaWritingList), hiraganaWritingList.length) },
        { label: "Katakana", percent: completionPercent(completedKana(katakanaWritingList), katakanaWritingList.length) },
        { label: "Kanji", percent: completionPercent(knownKanji, joyoKanji.length) },
        { label: "Grammar", percent: completionPercent(learnedGrammar, grammarPoints.length) },
        { label: "Vocabulary", percent: completionPercent(learnedVocabulary, VOCABULARY_TOTAL) },
        { label: "Conjugation", percent: completionPercent(learnedConjugations, VERB_TOTAL) },
    ];

    return (
        <div className={styles.scriptProgress} role="group" aria-label="Script learning progress">
            <div className={styles.scriptProgressTrack}>
                {scripts.map((script) => (
                    <div key={script.label} className={styles.scriptProgressZone} aria-label={`${script.label} completion: ${script.percent}%`}>
                        <span className={styles.scriptProgressFill} style={{ width: `${script.percent}%` }} aria-hidden="true" />
                        <span className={styles.scriptProgressLabel}>{script.label}</span>
                        <strong className={styles.scriptProgressPercent}>{script.percent}%</strong>
                    </div>
                ))}
            </div>
        </div>
    );
}

function HomePage() {
    const location = useLocation();
    const { status: authStatus, isSigningOut } = useAuth();
    const continueLearning = useContinueLearning();
    const showHero =
        authStatus === "unauthenticated" ||
        (authStatus === "authenticated" && continueLearning.isReady && !continueLearning.recommendation);

    useEffect(() => {
        if (!location.hash) return;
        const target = document.getElementById(location.hash.slice(1));
        if (!target) return;
        const prefersReducedMotion =
            typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        target.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
    }, [location.hash]);

    return (
        <div className={styles.page}>
            <Seo
                title="NihongoTech by TeqSeq | Learn Japanese in One Place"
                description="NihongoTech by TeqSeq is an all-in-one Japanese learning app - practise hiragana, katakana and kanji, build vocabulary and grammar, and take quizzes, all in one place instead of switching between apps."
                path="/"
                suffixBrand={false}
            />
            <Header />

            <main className={styles.main}>
                {showHero && (
                    <section className={styles.hero} aria-labelledby="hero-heading">
                        <span className={styles.heroEyebrow} aria-hidden="true">
                            日本語学習プラットフォーム
                        </span>
                        <h1 id="hero-heading" className={styles.heroTitle}>
                            Learn, practice and write Japanese
                        </h1>
                        <p className={styles.heroSubtitle}>
                            Guided lessons, writing practice, vocabulary, grammar and quizzes - all in one place.
                        </p>
                        <div className={styles.heroActions}>
                            <Link to="/lessons" className={styles.heroPrimaryButton}>
                                Start learning
                            </Link>
                            <Link to="/keyboard" className={styles.heroSecondaryButton}>
                                Open keyboard
                            </Link>
                        </div>
                    </section>
                )}

                <LearningStatsSection
                    visible={authStatus === "authenticated" && continueLearning.isReady && Boolean(continueLearning.recommendation)}
                />

                <ContinueLearningSection
                    authStatus={authStatus}
                    isSigningOut={isSigningOut}
                    {...continueLearning}
                />

                <section className={styles.pathSection} id="start-learning" aria-labelledby="start-learning-heading">
                    <div className={styles.pathSectionHeader}>
                        <h2 id="start-learning-heading" className={styles.pathSectionHeading}>
                            Start learning
                        </h2>
                        <p className={styles.pathSectionDescription}>
                            New to Japanese? Begin with the writing systems and essential grammar.
                        </p>
                    </div>
                    {authStatus === "authenticated" && continueLearning.isReady && Boolean(continueLearning.recommendation) && (
                        <ScriptProgressBar
                            kanaDrawCounts={continueLearning.kanaDrawCounts}
                            kanjiProgress={continueLearning.kanjiProgress}
                            grammarProgress={continueLearning.grammarProgress}
                            vocabularyProgress={continueLearning.vocabularyProgress}
                            conjugationProgress={continueLearning.conjugationProgress}
                        />
                    )}
                    <div className={styles.pathGridPrimary}>
                        {startLearningItems.map((item) => (
                            <PathCard key={item.to} item={item} />
                        ))}
                    </div>
                </section>

                <section className={styles.toolSection} aria-labelledby="integrations-heading">
                    <div className={styles.pathSectionHeader}>
                        <h2 id="integrations-heading" className={styles.pathSectionHeading}>
                            Integrations
                        </h2>
                        <p className={styles.pathSectionDescription}>
                            Connect other Japanese-learning tools you already use.
                        </p>
                    </div>
                    <div className={styles.toolGrid}>
                        {integrationItems.map((item) => (
                            <ToolCard key={item.to} item={item} />
                        ))}
                    </div>
                </section>

                <section className={styles.toolSection} aria-labelledby="tools-heading">
                    <div className={styles.pathSectionHeader}>
                        <h2 id="tools-heading" className={styles.pathSectionHeading}>
                            Tools
                        </h2>
                        <p className={styles.pathSectionDescription}>
                            Write, convert, translate and look up Japanese whenever you need help.
                        </p>
                    </div>
                    <div className={styles.toolGrid}>
                        {toolItems.map((item) => (
                            <ToolCard key={item.to} item={item} />
                        ))}
                    </div>

                    <div className={styles.toolGrid}>
                        {referenceItems.map((item) => (
                            <ToolCard key={item.to} item={item} />
                        ))}
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}

export default HomePage;
